import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { applyPropBuilder } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useCanSeeAdminInfo from "@store/Hooks/AccountCenter/useCanSeeAdminInfo";
import loadCreditInformation from "@store/Store/Data/CreditInformations/Handlers/LoadCreditInformations";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { focusStyles } from "@store/theme/common/mixins";
import { MENU, TYPOGRAPHY } from "@store/theme/design";
import isTheme from "@store/utilities/isTheme";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

interface OwnProps {
    percentage?: number;
    balance?: number;
    limit?: number;
}

const mapStateToProps = (state: StoreApplicationState) => {
    const stateContext = state.store.data.creditInformations;

    return {
        isLoading: stateContext.isLoading,
        creditInformation: stateContext.creditInformationModel,
    };
};
const mapDispatchToProps = {
    loadCreditInformation,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CreditUseIndicatorStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    moreInfosContainer?: InjectableCss;
    moreInfosBody?: InjectableCss;
    creditInfoText?: TypographyPresentationProps;
    indicatorLink?: LinkPresentationProps;
}

const StyledSpan = getStyledWrapper("span");
const size = 36;
const border = 4;
const halfSize = size / 2;
const doubleBorder = border * 2;

const halfStyles = () => {
    return css`
        position: absolute;
        top: -${border}px;
        left: -${border}px;
        width: calc(100% + ${doubleBorder}px);
        height: calc(100% + ${doubleBorder}px);
        border-radius: 50%;
        clip: rect(0, ${size}px, ${size}px, ${halfSize}px);

        &::before {
            content: "";
            position: absolute;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            clip: rect(0, ${halfSize}px, ${size}px, 0);
            border: ${border}px solid ${getColor("danger")};
            box-sizing: border-box;
        }
    `;
};

export const creditUseIndicatorStyles: CreditUseIndicatorStyles = {
    container: {
        css: css`
            padding: ${MENU.mobileButtonSpacing};
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            position: relative;
            width: ${size}px;
            height: ${size}px;
            ${({ theme }) => {
                if (isTheme(theme, "horizon")) {
                    return css`border: ${border}px solid ${getColor("primary")};`;
                }

                return css`border: ${border}px solid ${getColor("common.border")};`;
            }}
            
            border-radius: 50%;
            box-sizing: border-box;

            &::after {
                content: "";
                position: absolute;
                top: ${border}px;
                left: ${border}px;
                width: calc(100% - ${doubleBorder}px);
                height: calc(100% - ${doubleBorder}px);
                border-radius: inherit;
            }

            &:hover {
                > ${StyledSpan} {
                    display: inline-flex;
                }
            }
        `,
    },
    moreInfosContainer: {
        css: css`
            z-index: 1;
            width: 220px;
            position: absolute;
            display: none;
            justify-content: center;
            top: calc(100% + 14px);
            left: 50%;
            margin-left: -110px;
            margin-bottom: 11px;

            &::before {
                content: "";
                position: absolute;
                bottom: calc(100% - 1px);
                left: 50%;
                margin-left: -6px;
                border-width: 6px;
                border-style: solid;
                border-color: transparent transparent ${getColor("common.backgroundContrast")} transparent;
            }
        `,
    },
    moreInfosBody: {
        css: css`
            display: inline;
            padding: 10px;
            text-align: center;
            min-width: 100px;
            border-radius: 5px;
            background: ${getColor("common.backgroundContrast")};
            color: ${getColor("common.background")};
        `,
    },
    creditInfoText: {
        size: TYPOGRAPHY.size.smallest,
        weight: TYPOGRAPHY.weight.base,
    },
    indicatorLink: {
        css: css`
            ${focusStyles()}
            color: inherit;
        `,
    },
};

const styles = creditUseIndicatorStyles;
const Indicator: React.FC<OwnProps> = ({ percentage, balance, limit }) => {
    const canSeeAdminInfo = useCanSeeAdminInfo();
    const creditUsedPercentage = percentage || 0;
    const ratio = creditUsedPercentage / 100;
    const degree = ratio > 1 || ratio < 0 ? 360 : ratio * 360;
    const firstDegree = degree >= 180 ? 180 : degree;
    const halfDegree = degree - 180;
    const secondDegree = halfDegree <= 0 ? 0 : halfDegree;
    const creditPercentageText = creditUsedPercentage?.toString();

    const percentageTextSize = (percent: number) => {
        if (percent > 100 || percent < -100) {
            return css`
                font-size: ${TYPOGRAPHY.size.larger};
            `;
        }
        if (percent < 100 && percent >= 0) {
            return css`
                font-size: ${TYPOGRAPHY.size.tiny};
            `;
        }
        return css`
            font-size: ${TYPOGRAPHY.size.veryTiny};
        `;
    };

    const firstHalfStyles = {
        css: css`
            ${halfStyles()}

            &::before {
                transform: rotate(${firstDegree}deg);
            }
        `,
    };

    const secondHalfStyles = {
        css: css`
            ${halfStyles()}
            transform: rotate(180deg);

            &::before {
                transform: rotate(${secondDegree}deg);
            }
        `,
    };

    const percentageTextStyles = {
        css: css`
            ${percentageTextSize(creditUsedPercentage)}
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...firstHalfStyles}></StyledWrapper>
                <StyledWrapper {...secondHalfStyles}></StyledWrapper>
                <Typography {...percentageTextStyles}>
                    {creditUsedPercentage > 100 || creditUsedPercentage < -100 ? "!" : `${creditPercentageText}%`}
                </Typography>
                <StyledSpan {...styles.moreInfosContainer}>
                    <StyledSpan {...styles.moreInfosBody}>
                        {canSeeAdminInfo ? (
                            <>
                                <Typography as="p" {...styles.creditInfoText}>
                                    {translate("A/R Balance")}: <LocalizedCurrency amount={balance || 0} />
                                </Typography>
                                <Typography as="p" {...styles.creditInfoText}>
                                    {translate("Credit Limit")}: <LocalizedCurrency amount={limit || 0} />
                                </Typography>
                            </>
                        ) : null}
                        <Typography as="p" {...styles.creditInfoText}>
                            {translate("Credit used")}: {creditPercentageText}%
                        </Typography>
                    </StyledSpan>
                </StyledSpan>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const CreditUseIndicator: React.FC<Props & LinkProps> = ({
    loadCreditInformation,
    isLoading,
    creditInformation,
    ...otherProps
}) => {
    const { applyProp } = applyPropBuilder(otherProps, { component: "link" });
    const canClickOnIndicator = useCanSeeAdminInfo();
    const [creditHasError, setCreditHasError] = useState(false);
    const [creditLimit, setCreditLimit] = useState<number>();
    const [creditBalance, setCreditBalance] = useState<number>();
    const [creditPercentage, setCreditPercentage] = useState<number>(creditInformation?.creditUsedPercentage || 0);
    const showIndicator = creditInformation?.showIndicator;

    useEffect(() => {
        setCreditPercentage(creditInformation?.creditUsedPercentage || 0);
        setCreditBalance(creditInformation?.balance || 0);
        setCreditLimit(creditInformation?.creditLimit || 0);
    }, [creditInformation]);

    useEffect(() => {
        if (!creditInformation && !isLoading) {
            if (!creditHasError) {
                loadCreditInformation({ apiParameter: {}, onError: () => {setCreditHasError(true)} });
            }
        }
    }, [creditInformation, isLoading]);

    if (!creditInformation && !isLoading) {
        if (!creditHasError) {
            return <Indicator percentage={creditPercentage} balance={creditBalance} limit={creditLimit} />;
        }
    }

    if (!canClickOnIndicator && showIndicator) {
        return <Indicator percentage={creditPercentage} balance={creditBalance} limit={creditLimit} />;
    }

    if (showIndicator) {
        return (
            <Link href={applyProp("href")} {...styles.indicatorLink}>
                <Indicator percentage={creditPercentage} balance={creditBalance} limit={creditLimit} />
            </Link>
        );
    }

    return null;
};

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(CreditUseIndicator));
